import is from '@sindresorhus/is';
import type { Result } from 'true-myth';

import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type {
    Comment,
    CommentSubjectVersion,
    CreateCommentRequest,
    CreateReplyRequest,
    ResolveCommentRequest,
} from '../../data-contract/comment';
import { commentSchema, commentsSchema } from '../../data-contract/comment';

type CommentSubjectRoute = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly version: CommentSubjectVersion;
};

export type CommentsFetchers = {
    readonly createComment: (
        params: CommentSubjectRoute & {
            readonly body: CreateCommentRequest;
        },
    ) => Promise<Result<Comment, HttpFetcherError>>;
    readonly createReply: (
        params: CommentSubjectRoute & {
            readonly body: CreateReplyRequest;
            readonly commentId: string;
        },
    ) => Promise<Result<Comment, HttpFetcherError>>;
    readonly deleteComment: (
        params: CommentSubjectRoute & {
            readonly commentId: string;
        },
    ) => Promise<Result<unknown, HttpFetcherError>>;
    readonly fetchComments: (
        params: CommentSubjectRoute & {
            readonly language?: string;
        },
    ) => Promise<Result<readonly Comment[], HttpFetcherError>>;
    readonly fetchReplies: (
        params: CommentSubjectRoute & {
            readonly commentId: string;
        },
    ) => Promise<Result<readonly Comment[], HttpFetcherError>>;
    readonly resolveComment: (
        params: CommentSubjectRoute & {
            readonly body: ResolveCommentRequest;
            readonly commentId: string;
        },
    ) => Promise<Result<Comment, HttpFetcherError>>;
};

export type CommentsFetchersDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

type CommentsEndpointConfig = {
    readonly collectionPath: (route: CommentSubjectRoute) => `/${string}`;
};

const listSearchParams = (params: {
    readonly language: string | undefined;
    readonly version: CommentSubjectVersion;
}): Record<string, CommentSubjectVersion> =>
    is.undefined(params.language)
        ? { version: params.version }
        : { language: params.language, version: params.version };

const createConfiguredCommentsFetchers = (
    dependencies: CommentsFetchersDependencies,
    endpoints: CommentsEndpointConfig,
): CommentsFetchers => {
    const { cobrainerApiFetcher, validatingCobrainerApiFetcher } = dependencies;

    const fetchComments: CommentsFetchers['fetchComments'] = async (params) => {
        return validatingCobrainerApiFetcher.get(commentsSchema, {
            pathname: endpoints.collectionPath(params),
            requestOptions: {
                searchParams: listSearchParams({ language: params.language, version: params.version }),
            },
        });
    };

    const createComment: CommentsFetchers['createComment'] = async (params) => {
        const { body } = params;
        return validatingCobrainerApiFetcher.postJson(commentSchema, {
            pathname: endpoints.collectionPath({ ...params, version: body.version }),
            payload: body,
        });
    };

    const deleteComment: CommentsFetchers['deleteComment'] = async (params) => {
        const { commentId } = params;
        return cobrainerApiFetcher.delete({
            pathname: `${endpoints.collectionPath(params)}/${commentId}`,
        });
    };

    const resolveComment: CommentsFetchers['resolveComment'] = async (params) => {
        const { body, commentId } = params;
        return validatingCobrainerApiFetcher.postJson(commentSchema, {
            pathname: `${endpoints.collectionPath({ ...params, version: body.version })}/${commentId}/resolve`,
            payload: body,
        });
    };

    const fetchReplies: CommentsFetchers['fetchReplies'] = async (params) => {
        const { commentId } = params;
        return validatingCobrainerApiFetcher.get(commentsSchema, {
            expectedStatusCodes: [404],
            pathname: `${endpoints.collectionPath(params)}/${commentId}/replies`,
        });
    };

    const createReply: CommentsFetchers['createReply'] = async (params) => {
        const { body, commentId } = params;
        return validatingCobrainerApiFetcher.postJson(commentSchema, {
            pathname: `${endpoints.collectionPath(params)}/${commentId}/replies`,
            payload: body,
        });
    };

    return {
        createComment,
        createReply,
        deleteComment,
        fetchComments,
        fetchReplies,
        resolveComment,
    };
};

export const createJobProfileCommentsFetchers = (dependencies: CommentsFetchersDependencies): CommentsFetchers =>
    createConfiguredCommentsFetchers(dependencies, {
        collectionPath: ({ architectureId, itemId }) => `/job-architectures/${architectureId}/items/${itemId}/comments`,
    });

export const createProfileTaskCommentsFetchers = (dependencies: CommentsFetchersDependencies): CommentsFetchers =>
    createConfiguredCommentsFetchers(dependencies, {
        collectionPath: ({ itemId }) => `/work-tasks/${itemId}/comments`,
    });
